import { ModifierType } from 'features/menu/components/product-detail/modifier/types';
import { mapModifier } from 'features/menu/components/product-detail/modifier/utils';
import { QUANTITY_DEFAULTS } from 'features/menu/components/product-detail/utils';
import { SlotSelections } from 'features/menu/components/product-wizard/types';

import { IQuantity } from 'generated/graphql-gateway';
import { ILocalizedMenuEntry, IndexedMenu, PosServiceMode } from 'state/unified-menu/types';
import { isEntryAvailable, isItem } from 'state/unified-menu/utils';

export interface ModifierInformation {
  modifier: ILocalizedMenuEntry;
  modifierRemainingSelections: number;
  modifierQuantity?: IQuantity | null;
}

/**
 * Returns the modifier that require user selections,
 * and computes the remaining selections amount
 */
export const getRequiredModifierInformation = (
  menu: IndexedMenu<ILocalizedMenuEntry>,
  itemId: string,
  slotSelection: SlotSelections | undefined,
  posServiceMode: PosServiceMode
): ModifierInformation | null => {
  const item = menu.find(itemId);
  if (
    !item ||
    !isItem(item) ||
    item.options?.entries?.length !== 1 ||
    !isEntryAvailable(item.options.entries[0], posServiceMode, undefined)
  ) {
    return null;
  }

  const [{ entryId: modifierKey, quantity: modifierQuantity }] = item.options.entries;

  const mappedModifier = mapModifier(
    menu,
    modifierKey,
    item.options?.entries.length ?? 0,
    posServiceMode,
    modifierQuantity
  );

  if (!mappedModifier) {
    return null;
  }

  const modifierQtys = Object.values(slotSelection?.modifiers?.[modifierKey] ?? {});

  const selectedQuantity = modifierQtys.reduce((sum, quantity) => sum + quantity, 0);

  const quantityConstraints = modifierQuantity ?? {
    min: QUANTITY_DEFAULTS.min,
    max: QUANTITY_DEFAULTS.max,
  };
  const modifierMinAmount = quantityConstraints.min ?? 0;

  // Checkbox can be visually selected even if the user did not interact.
  const effectiveSelectedQuantity =
    mappedModifier.type === ModifierType.CHECKBOX && modifierQtys.length === 0
      ? 1
      : selectedQuantity;

  const modifierRemainingSelections = Math.max(modifierMinAmount - effectiveSelectedQuantity, 0);

  const modifier = menu.get(modifierKey);
  return {
    modifier,
    modifierRemainingSelections,
    modifierQuantity: quantityConstraints,
  };
};
